import {
  isCategoryType,
  isNewCarFuelType,
  isNewCarSortType,
} from '@/apis/server/new-car/v1/utils';
import {
  AUTHORISED_DISTRIBUTORS,
  PARALLEL_IMPORTERS,
} from '@/constants/global';
import { PHP_MAX_INTEGER_LIMIT } from '@/constants/system';

import type {
  CollectSearchDataConfig,
  OnSearchData,
  SearchFilterTypeMap,
} from './types';

export const convertToCamelCase = (input: string) => input
  .toLowerCase()
  .replace(/[^a-zA-Z0-9]+(.)/g, (match, char) => char.toUpperCase());

export const isInteger = (
  input: unknown,
  range: [lower: number, upper: number],
) => (
  Number.isInteger(input)
  && typeof input === 'number'
  && input >= range[0]
  && input <= range[1]
);

export const categoryTypeToText = (category: 'ad' | 'pi' | 'a' | 'b') => {
  switch (category) {
    case 'ad':
      return 'authorised-distributor';
    case 'pi':
      return 'parallel-importer';
    case 'a':
      return 'cat-a-coe';
    case 'b':
      return 'cat-b-coe';
    default:
      return 'any';
  }
};

// validate and parse query string from url and convert to data
export const collectSearchData: (
  config: CollectSearchDataConfig
) => OnSearchData = ({
  searchParams,
}) => {
  const make = typeof searchParams.make === 'string'
    ? searchParams.make.trim()
    : undefined;
  const category = isCategoryType(searchParams.category)
    ? searchParams.category
    : undefined;
  const coeCategory = typeof searchParams.coe_category === 'string'
    ? searchParams.coe_category.trim()
    : undefined;
  const country = typeof searchParams.country === 'string'
    ? searchParams.country.trim()
    : undefined;
  const dealerId = typeof searchParams.dealer_id === 'string'
    && Number.isInteger(+(searchParams.dealer_id))
    ? +(searchParams.dealer_id)
    : undefined;
  const dealerType = searchParams.dealer_type === AUTHORISED_DISTRIBUTORS
    || searchParams.dealer_type === PARALLEL_IMPORTERS
    ? searchParams.dealer_type
    : undefined;
  const fuelType = isNewCarFuelType(searchParams.fuel_type)
    ? searchParams.fuel_type
    : undefined;
  const order = searchParams.order === 'asc'
    || searchParams.order === 'desc'
    ? searchParams.order
    : undefined;
  const q = typeof searchParams.q === 'string'
    && searchParams.q.length <= 50
    ? searchParams.q.trim()
    : undefined;
  const year = typeof searchParams.year === 'string'
    && isInteger(+(searchParams.year), [2002, 9999])
    ? searchParams.year
    : undefined;
  const sortBy = isNewCarSortType(searchParams.sort_by)
    ? searchParams.sort_by
    : undefined;
  const transmission = typeof searchParams.transmission === 'string'
    ? searchParams.transmission.trim()
    : undefined;
  const vehicleType = (typeof searchParams.vehicle_type === 'string'
    && searchParams.vehicle_type !== 'any')
    ? searchParams.vehicle_type.trim()
    : undefined;
  const page = typeof searchParams.page === 'string'
    && isInteger(+(searchParams.page), [1, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.page)
    : undefined;
  const limit = typeof searchParams.limit === 'string'
    && isInteger(+(searchParams.limit), [15, 99])
    ? +(searchParams.limit)
    : undefined;
  const maxDepreciation = typeof searchParams.max_depreciation === 'string'
    && isInteger(+(searchParams.max_depreciation), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.max_depreciation)
    : undefined;
  const maxDriveRange = typeof searchParams.max_drive_range === 'string'
    && isInteger(+(searchParams.max_drive_range), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.max_drive_range)
    : undefined;
  const maxEngineCapacity = typeof searchParams.max_engine_capacity === 'string'
    && isInteger(+(searchParams.max_engine_capacity), [0, 9999])
    ? +(searchParams.max_engine_capacity)
    : undefined;
  const maxFuelEconomy = typeof searchParams.max_fuel_economy === 'string'
    && isInteger(+(searchParams.max_fuel_economy), [0, 9999])
    ? +(searchParams.max_fuel_economy)
    : undefined;
  const maxOmv = typeof searchParams.max_omv === 'string'
    && isInteger(+(searchParams.max_omv), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.max_omv)
    : undefined;
  const maxPowerOutput = typeof searchParams.max_power_output === 'string'
    && isInteger(+(searchParams.max_power_output), [0, 9999])
    ? +(searchParams.max_power_output)
    : undefined;
  const maxEnergyConsumption = typeof searchParams.max_energy_consumption
    === 'string'
    && isInteger(+(searchParams.max_energy_consumption), [0, 9999])
    ? +(searchParams.max_energy_consumption)
    : undefined;
  const maxPrice = typeof searchParams.max_price === 'string'
    && isInteger(+(searchParams.max_price), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.max_price)
    : undefined;
  const minDepreciation = typeof searchParams.min_depreciation === 'string'
    && isInteger(+(searchParams.min_depreciation), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.min_depreciation)
    : undefined;
  const minDriveRange = typeof searchParams.min_drive_range === 'string'
    && isInteger(+(searchParams.min_drive_range), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.min_drive_range)
    : undefined;
  const minEngineCapacity = typeof searchParams.min_engine_capacity === 'string'
    && isInteger(+(searchParams.min_engine_capacity), [0, 9999])
    ? +(searchParams.min_engine_capacity)
    : undefined;
  const minFuelEconomy = typeof searchParams.min_fuel_economy === 'string'
    && isInteger(+(searchParams.min_fuel_economy), [0, 9999])
    ? +(searchParams.min_fuel_economy)
    : undefined;
  const minOmv = typeof searchParams.min_omv === 'string'
    && isInteger(+(searchParams.min_omv), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.min_omv)
    : undefined;
  const minPowerOutput = typeof searchParams.min_power_output === 'string'
    && isInteger(+(searchParams.min_power_output), [0, 9999])
    ? +(searchParams.min_power_output)
    : undefined;
  const minEnergyConsumption = typeof searchParams.min_energy_consumption
    === 'string'
    && isInteger(+(searchParams.min_energy_consumption), [0, 9999])
    ? +(searchParams.min_energy_consumption)
    : undefined;
  const minPrice = typeof searchParams.min_price === 'string'
    && isInteger(+(searchParams.min_price), [0, PHP_MAX_INTEGER_LIMIT])
    ? +(searchParams.min_price)
    : undefined;

  return {
    query: {
      make,
      category,
      coeCategory,
      country,
      dealerId,
      dealerType,
      fuelType,
      order,
      q,
      year,
      sortBy,
      transmission,
      vehicleType,
      page,
      limit,
      maxDepreciation,
      maxDriveRange,
      maxEngineCapacity,
      maxEnergyConsumption,
      maxFuelEconomy,
      maxOmv,
      maxPowerOutput,
      maxPrice,
      minDepreciation,
      minDriveRange,
      minEngineCapacity,
      minEnergyConsumption,
      minFuelEconomy,
      minOmv,
      minPowerOutput,
      minPrice,
    },
  };
};

export const collectTargetMapping: (
  query: SearchFilterTypeMap,
  brandList: string[],
) => Record<string, string | string[]> = (
  query,
  brandList,
) => {
  const lowerCaseBrandList = brandList.map(brand => brand.toLowerCase());
  const target: Record<string, string[]> = {
    newcarMake: [],
    newcarType: [],
    newcarPrice: [],
    newcarCategory: [],
  };

  if (query.make) {
    target.newcarMake.push(query.make.toLowerCase().split(' ').join('-'));
  }

  if (query.q) {
    if (lowerCaseBrandList.includes(query.q.toLowerCase())) {
      target.newcarMake.push(query.q.toLowerCase().split(' ').join('-'));
    }

    if (query.q.toLowerCase().includes('convertible')) {
      target.newcarType.push('convertible');
    }

    if (query.q.toLowerCase().includes('coupe')) {
      target.newcarType.push('coupe');
    }

    if (query.q.toLowerCase().includes('crossover')) {
      target.newcarType.push('crossover');
    }

    if (query.q.toLowerCase().includes('van')) {
      target.newcarType.push('van');
    }

    if (query.q.toLowerCase().includes('bus')) {
      target.newcarType.push('bus');
    }

    if (query.q.toLowerCase().includes('truck')) {
      target.newcarType.push('truck');
    }

    if (query.q.toLowerCase().includes('diesel')) {
      target.newcarType.push('diesel');
    }
  }

  if (query.maxPrice) {
    target.newcarPrice.push(`max${(query.maxPrice / 1000).toString()}k`);

    if (query.maxPrice <= 150000) {
      target.newcarType.push('budget-car');
    }
  }

  if (query.minPrice) {
    if (query.minPrice >= 300000) {
      target.newcarType.push('luxury-car');
    }
  }

  if (query.minPrice && query.maxPrice) {
    if (query.minPrice >= 160000 && query.maxPrice <= 300000) {
      target.newcarType.push('mid-range-car');
    }
  }

  if (query.vehicleType) {
    if (query.vehicleType === 'passenger') {
      target.newcarType.push('passengers-car');
    } else if (query.vehicleType === 'commercial') {
      target.newcarType.push('commercial-vehicles');
    } else if (query.vehicleType === 'sports') {
      target.newcarType.push('sports-car');
    } else if (query.vehicleType === 'luxury') {
      target.newcarType.push('luxury-sedan');
    } else {
      target.newcarType.push(
        query.vehicleType.toLowerCase().split(' ').join('-'),
      );
    }
  }

  if (query.category) {
    target.newcarCategory.push(categoryTypeToText(query.category));
  }

  Object.keys(target).forEach((key) => {
    if (target[key].length === 0) {
      delete target[key];
    }
  });

  return target;
};
